import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, RefreshCw, ChevronRight, Settings } from 'lucide-react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const ENTITY_PALETTE = {
  customer: 'text-info bg-info/10',
  supplier: 'text-warning bg-warning/10',
  partner: 'text-violet-700 bg-violet-100 dark:bg-violet-900/30',
  employee: 'text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30',
};

export default function MatchQualityWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const fetchReport = async () => {
    try {
      const r = await api.get('/bank-matching/quality-report?days=30');
      setData(r.data);
    } catch (e) {
      // Hide widget on 403 (not admin/manager) or other errors
      if (e.response?.status !== 403) console.warn('match-quality fetch', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, []);

  if (loading) return null;
  if (!data) return null;
  const totals = data.totals || {};
  const totalActivity = (totals.auto_matched || 0) + (totals.manual_match || 0);
  if (totalActivity === 0) return null; // Hide on tenants with zero matching activity

  const refresh = async () => { setRefreshing(true); await fetchReport(); setRefreshing(false); };

  return (
    <Card className="border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50/50 to-fuchsia-50/30 dark:from-stone-900 dark:to-stone-900" data-testid="match-quality-widget">
      <CardContent className="pt-4 space-y-3 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
              <TrendingUp size={14} className="text-violet-700 dark:text-violet-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold font-outfit">Match-Quality Report · last {data.window_days}d</h3>
              <p className="text-[10px] text-muted-foreground">
                {totals.auto_matched || 0} auto-matched · {totals.auto_confirmed || 0} auto-confirmed · {totals.pending_review || 0} pending
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={refresh} disabled={refreshing} data-testid="refresh-quality">
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs"
              onClick={() => navigate('/bank-matching-settings')} title="Settings" data-testid="open-match-settings">
              <Settings size={12} />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs"
              onClick={() => navigate('/reconciliation')} data-testid="view-reconciliation">
              View <ChevronRight size={12} className="ml-1" />
            </Button>
          </div>
        </div>

        {/* Headline KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <div className="rounded-md bg-white dark:bg-stone-800 p-2 text-center border border-border" data-testid="kpi-auto-rate">
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Auto-confirm</p>
            <p className="text-xl font-bold text-violet-700 dark:text-violet-400">{data.auto_confirm_rate}%</p>
          </div>
          <div className="rounded-md bg-white dark:bg-stone-800 p-2 text-center border border-border">
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">High conf</p>
            <p className="text-xl font-bold text-success">{data.confidence_distribution?.high || 0}</p>
          </div>
          <div className="rounded-md bg-white dark:bg-stone-800 p-2 text-center border border-border">
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Rejected</p>
            <p className="text-xl font-bold text-rose-600">{totals.rejected || 0}</p>
          </div>
        </div>

        {/* By entity type */}
        {data.by_entity_type && Object.keys(data.by_entity_type).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.by_entity_type).map(([et, count]) => (
              <span key={et}
                className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider ${ENTITY_PALETTE[et] || 'bg-muted'}`}
                data-testid={`entity-stat-${et}`}>
                {et}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Top entities */}
        {data.top_entities && data.top_entities.length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Top entities by match volume</p>
            <ul className="space-y-0.5">
              {data.top_entities.slice(0, 5).map((e, i) => (
                <li key={`${e.entity_type}-${e.id}-${i}`} className="flex items-center justify-between text-xs" data-testid={`top-entity-${i}`}>
                  <span className="truncate">
                    <span className={`text-[9px] uppercase font-bold mr-1.5 px-1 py-0.5 rounded ${ENTITY_PALETTE[e.entity_type] || 'bg-muted'}`}>{e.entity_type}</span>
                    {e.name}
                  </span>
                  <span className="font-mono font-bold ml-2 shrink-0">{e.matches}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
